import { useEffect, useState } from 'react'
import {
  fetchDeviceNotificationPreferences,
  fetchUserNotificationPreferences,
  setDeviceNotificationPreference,
  setUserNotificationPreference,
  type DeviceNotificationPreferences,
  type UserNotificationPreferences,
} from '../../api/ws'

// Notification preferences sheet. Two sections:
//
//   1. "On this device" — per-device toggles for all four categories.
//      Lets a user mute one device while keeping pushes alive on the
//      others. Backs device_notification_preferences.
//
//   2. "All my devices" — per-human toggles for the two categories
//      that aren't agent-scoped (household activity, announcements).
//      The other two (permission_requests, mentions) are handled by
//      the per-agent prefs on the agent's detail screen, so they're
//      not duplicated here at the human level. Backs
//      user_notification_preferences.
//
// Both sections AND-compose with each other AND with the per-agent
// prefs at delivery time. Absent rows default to enabled, so a fresh
// install gets every category by default.

type DeviceRow = {
  category: keyof DeviceNotificationPreferences
  label: string
  description: string
}

const DEVICE_ROWS: DeviceRow[] = [
  {
    category: 'permission_requests',
    label: 'Permission requests',
    description: 'When agents ask you to approve a tool call or action.',
  },
  {
    category: 'mentions',
    label: 'Mentions',
    description: 'When an agent or household member @-mentions you.',
  },
  {
    category: 'household_activity',
    label: 'Household activity',
    description: 'Hosts enrolled or removed; members joined or removed.',
  },
]

type UserRow = {
  category: keyof UserNotificationPreferences
  label: string
  description: string
}

const USER_ROWS: UserRow[] = [
  {
    category: 'household_activity',
    label: 'Household activity',
    description: 'Off here = no household-activity push on any of your devices.',
  },
]

export function NotificationPreferencesSheet() {
  const [device, setDevice] = useState<DeviceNotificationPreferences | null>(null)
  const [user, setUser] = useState<UserNotificationPreferences | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    Promise.all([
      fetchDeviceNotificationPreferences(),
      fetchUserNotificationPreferences(),
    ])
      .then(([d, u]) => {
        if (cancelled) return
        if (d.error) setError(d.error)
        else if (d.preferences) setDevice(d.preferences)
        if (u.error) setError(u.error)
        else if (u.preferences) setUser(u.preferences)
      })
      .catch((e: Error) => {
        if (cancelled) return
        setError(e.message)
      })
    return () => {
      cancelled = true
    }
  }, [])

  async function toggleDevice(category: keyof DeviceNotificationPreferences) {
    if (!device) return
    const next = !device[category]
    const busyKey = `device:${category}`
    setBusy(busyKey)
    setError(null)
    setDevice({ ...device, [category]: next })
    try {
      const r = await setDeviceNotificationPreference({ category, enabled: next })
      if (r.error) {
        setDevice((p) => (p ? { ...p, [category]: !next } : p))
        setError(r.error)
        return
      }
      if (r.preferences) setDevice(r.preferences)
    } catch (e) {
      setDevice((p) => (p ? { ...p, [category]: !next } : p))
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(null)
    }
  }

  async function toggleUser(category: keyof UserNotificationPreferences) {
    if (!user) return
    const next = !user[category]
    const busyKey = `user:${category}`
    setBusy(busyKey)
    setError(null)
    setUser({ ...user, [category]: next })
    try {
      const r = await setUserNotificationPreference({ category, enabled: next })
      if (r.error) {
        setUser((p) => (p ? { ...p, [category]: !next } : p))
        setError(r.error)
        return
      }
      if (r.preferences) setUser(r.preferences)
    } catch (e) {
      setUser((p) => (p ? { ...p, [category]: !next } : p))
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(null)
    }
  }

  return (
    <div style={{ padding: '12px 16px', fontSize: 14 }}>
      {error && (
        <div
          style={{
            background: '#fff0f0',
            border: '1px solid #f5a5a5',
            color: '#900',
            padding: 8,
            borderRadius: 4,
            marginBottom: 12,
            fontSize: 13,
          }}
        >
          {error}
        </div>
      )}

      <SectionHeader title="On this device" />
      <p style={{ opacity: 0.65, marginTop: 0, fontSize: 13 }}>
        These toggles only affect this device. Per-agent muting is
        managed in the agent's detail screen.
      </p>

      {device === null ? (
        <p style={{ opacity: 0.6 }}>Loading…</p>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {DEVICE_ROWS.map((r) => (
            <ToggleRow
              key={r.category}
              label={r.label}
              description={r.description}
              enabled={device[r.category]}
              isBusy={busy === `device:${r.category}`}
              onToggle={() => void toggleDevice(r.category)}
            />
          ))}
        </ul>
      )}

      <SectionHeader title="All my devices" style={{ marginTop: 24 }} />
      <p style={{ opacity: 0.65, marginTop: 0, fontSize: 13 }}>
        These toggles apply across every device you're signed in on.
        Off here overrides any per-device "on" setting above.
      </p>

      {user === null ? (
        <p style={{ opacity: 0.6 }}>Loading…</p>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {USER_ROWS.map((r) => (
            <ToggleRow
              key={r.category}
              label={r.label}
              description={r.description}
              enabled={user[r.category]}
              isBusy={busy === `user:${r.category}`}
              onToggle={() => void toggleUser(r.category)}
            />
          ))}
        </ul>
      )}
    </div>
  )
}

function SectionHeader({
  title,
  style,
}: {
  title: string
  style?: React.CSSProperties
}) {
  return (
    <h3
      style={{
        margin: '4px 0 4px',
        fontSize: 13,
        opacity: 0.7,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
        ...style,
      }}
    >
      {title}
    </h3>
  )
}

function ToggleRow({
  label,
  description,
  enabled,
  isBusy,
  onToggle,
}: {
  label: string
  description: string
  enabled: boolean
  isBusy: boolean
  onToggle: () => void
}) {
  return (
    <li
      style={{
        padding: '12px 0',
        borderBottom: '1px solid #eee',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600 }}>{label}</div>
        <div style={{ opacity: 0.6, fontSize: 12, marginTop: 2 }}>
          {description}
        </div>
      </div>
      <label
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          cursor: isBusy ? 'wait' : 'pointer',
          opacity: isBusy ? 0.6 : 1,
        }}
      >
        <input
          type="checkbox"
          checked={enabled}
          disabled={isBusy}
          onChange={onToggle}
          style={{ width: 22, height: 22 }}
        />
      </label>
    </li>
  )
}
